/** Appointment calls. Shapes follow workers/api/src/home/appointments.ts on `main`. */
import type { ApiClient } from "./client";
import type { Subject } from "./followups";

export interface AppointmentItem {
  id: string;
  source: "pt" | "trial" | "appointment";
  kind: "PT" | "Trial" | "Assessment" | "Tour" | "Consultation";
  startsAt: number;
  endsAt: number;
  branchId: string | null;
  person: Subject;
  /** `name` is null unless the role holds a name lever (D57). */
  staff: { id: string; name: string | null } | null;
  status: "booked" | "in" | "no_show" | "done" | "cancelled";
  /** The path that changes this item (server-provided). */
  route?: string;
}
export interface AppointmentsResult { date?: string; items: AppointmentItem[]; omitted: string[]; truncated: boolean }

export async function fetchAppointments(c: ApiClient, s: { date: string; branchId?: string }): Promise<AppointmentsResult> {
  const r = await c.get<{ date?: string; items?: AppointmentItem[]; appointments?: AppointmentItem[]; omitted?: string[]; truncated?: boolean }>("/appointments", { date: s.date, branchId: s.branchId });
  return { date: r.date, items: r.items ?? r.appointments ?? [], omitted: r.omitted ?? [], truncated: r.truncated ?? false };
}

export type AppointmentAction = "arrived" | "done" | "no_show" | "cancelled";

/** Which route moves an appointment on, by source (the feed tells the app which source an item is). */
export function updateAppointment(c: ApiClient, a: AppointmentItem, action: AppointmentAction, nowIso: string) {
  switch (a.source) {
    case "pt":
      if (action === "done") return c.post<unknown>(`/pt/sessions/${a.id}/complete`);
      if (action === "no_show") return c.post<unknown>(`/pt/sessions/${a.id}/no-show`);
      return c.post<unknown>(`/pt/sessions/${a.id}/cancel`);
    case "trial":
      if (action === "no_show") return c.patch<unknown>(`/leads/${a.person.id}`, { trialNoShowAt: nowIso });
      if (action === "done" || action === "arrived") return c.patch<unknown>(`/leads/${a.person.id}`, { trialDoneAt: nowIso });
      return c.patch<unknown>(`/leads/${a.person.id}`, { trialAt: null });
    case "appointment":
      return c.post<unknown>(`/appointments/${a.id}/status`, { status: action });
  }
}

/** Reschedule by source. `date`/`start`/`end` are the branch-local wall clock (PT only); `startsAtMs` is the instant. */
export function rescheduleAppointment(c: ApiClient, a: AppointmentItem, to: { startsAtMs: number; date: string; start: string; end: string }) {
  switch (a.source) {
    case "pt": return c.post<unknown>(`/pt/sessions/${a.id}/reschedule`, { date: to.date, start: to.start, end: to.end });
    case "trial": return c.patch<unknown>(`/leads/${a.person.id}`, { trialAt: new Date(to.startsAtMs).toISOString() });
    case "appointment": return c.patch<unknown>(`/appointments/${a.id}`, { startsAt: to.startsAtMs });
  }
}

export type NewAppointmentKind = "assessment" | "tour" | "consultation";
export interface NewAppointment { kind: NewAppointmentKind; leadId?: string; memberId?: string; startsAt: number; staffId?: string | null; branchId?: string; durationMin?: number; notes?: string }
export const createAppointment = (c: ApiClient, v: NewAppointment) => c.post<{ appointment: AppointmentItem }>("/appointments", v);
